import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth';
import { captureEvent } from '../lib/analytics';
import GoogleAuthButton from '../components/GoogleAuthButton';
import './AuthPage.css';

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(
    searchParams.get('error') === 'oauth'
      ? 'Google sign-in was cancelled or did not complete. Please try again.'
      : ''
  );
  const [loading, setLoading] = useState(false);

  // Read return URL saved by FitResultsPage (keep in sessionStorage — HomeRedirect
  // also checks it as a safety net in case Better Auth does a full-page redirect).
  const returnUrl = sessionStorage.getItem('bikendure_return_url') ?? '/';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await authClient.signIn.email({
        email,
        password,
        callbackURL: returnUrl,
      });
      if (result.error) {
        setError(result.error.message ?? 'Sign-in failed. Please try again.');
      } else {
        sessionStorage.removeItem('bikendure_return_url');
        captureEvent('user_signed_in', { method: 'email' });
        navigate(returnUrl, { replace: true });
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.png" alt="Bikendure" className="auth-logo-img" />
          <span className="auth-logo-name">Bikendure</span>
        </div>

        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">Sign in to your account</p>

        <GoogleAuthButton mode="sign-in" />

        <div className="auth-divider">
          <span className="auth-divider-text">or use email</span>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label htmlFor="email" className="auth-label">Email</label>
            <input
              id="email"
              type="email"
              className="auth-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password" className="auth-label">Password</label>
            <input
              id="password"
              type="password"
              className="auth-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
            />
          </div>

          {error && <p className="auth-error">{error}</p>}

          <button type="submit" className="auth-submit auth-submit--secondary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="auth-footer-text">
          Don't have an account?{' '}
          <Link to="/sign-up" className="auth-link">Sign up</Link>
        </p>
      </div>
    </div>
  );
};

export default SignInPage;
